import { useCallback, useMemo, useState } from 'preact/hooks';
import type { OverlayId, RouterApi, ScreenId, ScreenParams, StackFrame, TabId } from './types';
import { isTabId } from './types';

export function useAppRouter(initialTab: TabId = 'feed'): RouterApi {
  const [tab, setTab] = useState<TabId>(initialTab);
  const [tabParams, setTabParams] = useState<ScreenParams>({});
  const [stack, setStack] = useState<readonly StackFrame[]>([]);
  const [overlay, setOverlay] = useState<OverlayId | null>(null);

  const go = useCallback((screen: TabId | ScreenId, params: ScreenParams = {}) => {
    if (isTabId(screen)) {
      setTab(screen);
      setTabParams(params);
      setStack([]);
      return;
    }
    setStack((prev) => [...prev, { screen, params }]);
  }, []);

  const back = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const resetTo = useCallback((next: TabId) => {
    setTab(next);
    setTabParams({});
    setStack([]);
  }, []);

  const openOverlay = useCallback((id: OverlayId) => setOverlay(id), []);
  const closeOverlay = useCallback(() => setOverlay(null), []);

  const top = stack.length > 0 ? (stack[stack.length - 1] ?? null) : null;

  return useMemo<RouterApi>(
    () => ({ tab, tabParams, stack, overlay, top, go, back, resetTo, openOverlay, closeOverlay }),
    [tab, tabParams, stack, overlay, top, go, back, resetTo, openOverlay, closeOverlay],
  );
}
